import { LinkProps } from '@tanstack/react-router';

import { CountryCode } from 'common/api-types/common';
import {
  BETTER_TEST_MARKET_TENANT_ID,
  CI_TEST_TENANT_ID,
  DASHMART_TENANT_ID,
  DASHMART_TEST_TENANT_ID,
  DELIVEROO_TENANT_ID_DEV,
  DELIVEROO_TENANT_ID_PROD,
  DELIVEROO_TENANT_ID_PROD_TEST,
  DOORDASH_DMAAS_TENANT_ID,
  HUIPPUKIVA_TENANT_ID,
  WOLT_GLOBAL_TENANT_ID,
  WOLT_MARKET_TENANT_ID,
} from 'common/constants';
import { ExtractStrict } from 'common/types';

type RemoveLeadingSlash<T extends string> = T extends `/${infer R}` ? R : never;
type ExcludeEmptyString<T extends string> = T extends '' ? never : T;
type ExtractSingleLevelPaths<T extends string> = T extends `${infer Prefix}/${infer Rest}`
  ? Rest extends never
    ? Prefix
    : never
  : T;
type Links = NonNullable<LinkProps['to']>;
type RootRoutePath = ExtractSingleLevelPaths<ExcludeEmptyString<RemoveLeadingSlash<Links>>>;

export type TenantRoute = ExtractStrict<
  RootRoutePath,
  // venue-operations
  | 'tasks'
  | 'receiving'
  | 'ops-receiving'
  | 'shrinkage'
  | 'inventory-adjustments'
  | 'bin-locations'
  | 'stock-count'
  | 'product-average-weight'
  | 'venue-settings'

  // procurement
  | 'purchase-orders'
  | 'vendors'
  | 'finance'
  | 'override-management'

  // inventory-replenishment
  | 'forecast'
  | 'forecast-setup'
  | 'events'
  | 'product-relationship'
  | 'safety-stock-setup'
  | 'ordering-automation'
  | 'weekly-trends'
  | 'purchase-order-budgets'

  // pricing-and-promotions
  | 'pricing'
  | 'pricing-settings'

  // selection
  | 'catalog'
  | 'merchant-catalog'
  | 'product-families'
  | 'assortment'
  | 'selection-recommendations'
  | 'vsa'

  // others
  | 'analytics'
  | 'venues'
  | 'sales-venues'
  | 'warehouses'
  | 'user-settings'
  | 'access-management'
  | 'product-investigation'
>;

type UserRoles = Array<'user' | 'developer_wm'>;

type DefaultTenant = {
  countryCodes?: CountryCode[];
  landingRoute: TenantRoute;
  routes: Record<TenantRoute, UserRoles>;
  urls?: Record<string, UserRoles>;
};

export type Tenant = DefaultTenant & {
  id: string;
  code: string;
  name: string;
};

type Tenants = Record<string, Tenant>;

const USER_GUIDE_URI = 'https://woltwide.atlassian.net/l/c/9Ge5g0Uh';
const VITE_MERCHANT_ADMIN_URI = import.meta.env.VITE_MERCHANT_ADMIN_URI;
const ADMIN_URI = __ADMIN_URI__;

const defaultTenantConfig: DefaultTenant = {
  landingRoute: 'receiving',
  routes: {
    // venue-operations
    'tasks': ['user'],
    'receiving': ['user'],
    'ops-receiving': ['user'],
    'shrinkage': ['user'],
    'inventory-adjustments': ['user'],
    'bin-locations': ['user'],
    'stock-count': ['user'],
    'product-average-weight': ['user'],
    'venue-settings': [],

    // procurement
    'purchase-orders': ['user'],
    'vendors': ['user'],
    'finance': [],
    'override-management': [],

    // inventory-replenishment
    'forecast': [],
    'forecast-setup': [],
    'events': [],
    'product-relationship': [],
    'safety-stock-setup': [],
    'ordering-automation': [],
    'weekly-trends': [],
    'purchase-order-budgets': [],

    // selection
    'catalog': ['user'],
    'merchant-catalog': [],
    'product-families': ['user'],
    'assortment': ['user'],
    'selection-recommendations': [],
    'vsa': ['developer_wm'],

    // pricing-and-promotions
    'pricing': [],
    'pricing-settings': [],

    // others
    'analytics': [],
    'venues': ['user'],
    'sales-venues': ['user'],
    'warehouses': ['user'],
    'user-settings': ['user'],
    'access-management': ['user'],
    'product-investigation': [],
  },
};

const deliverooTenantConfig: DefaultTenant = {
  landingRoute: 'receiving',
  routes: {
    // venue-operations
    'tasks': ['user'],
    'receiving': ['user'],
    'ops-receiving': ['user'],
    'shrinkage': ['user'],
    'inventory-adjustments': ['user'],
    'bin-locations': ['user'],
    'stock-count': ['user'],
    'product-average-weight': ['user'],
    'venue-settings': ['user'],

    // procurement
    'purchase-orders': ['user'],
    'vendors': ['user'],
    'finance': ['user'],
    'override-management': ['user'],

    // inventory-replenishment
    'forecast': ['user'],
    'forecast-setup': ['user'],
    'events': ['user'],
    'product-relationship': ['user'],
    'safety-stock-setup': ['user'],
    'ordering-automation': ['user'],
    'weekly-trends': ['user'],
    'purchase-order-budgets': ['user'],

    // selection
    'catalog': ['user'],
    'merchant-catalog': [],
    'product-families': ['user'],
    'assortment': ['user'],
    'selection-recommendations': [],
    'vsa': ['developer_wm'],

    // pricing-and-promotions
    'pricing': [],
    'pricing-settings': [],

    // others
    'analytics': [],
    'venues': ['user'],
    'sales-venues': ['user'],
    'warehouses': ['user'],
    'user-settings': ['user'],
    'access-management': ['user'],
    'product-investigation': [],
  },
};

const tenantConfig: Tenants = {
  [HUIPPUKIVA_TENANT_ID]: {
    id: HUIPPUKIVA_TENANT_ID,
    name: 'Huippukiva',
    code: 'HUIPPUKIVA',
    countryCodes: ['FIN'],
    landingRoute: 'receiving',
    routes: {
      // venue-operations
      'tasks': ['user'],
      'receiving': ['user'],
      'ops-receiving': ['user'],
      'shrinkage': ['user'],
      'inventory-adjustments': ['user'],
      'bin-locations': ['user'],
      'stock-count': ['user'],
      'product-average-weight': ['user'],
      'venue-settings': [],

      // procurement
      'purchase-orders': ['user'],
      'vendors': ['user'],
      'finance': [],
      'override-management': [],

      // inventory-replenishment
      'forecast': [],
      'forecast-setup': [],
      'events': [],
      'product-relationship': [],
      'safety-stock-setup': [],
      'ordering-automation': [],
      'weekly-trends': [],
      'purchase-order-budgets': [],

      // selection
      'catalog': ['user'],
      'merchant-catalog': [],
      'product-families': ['user'],
      'assortment': ['user'],
      'selection-recommendations': [],
      'vsa': ['developer_wm'],

      // pricing-and-promotions
      'pricing': [],
      'pricing-settings': [],

      // others
      'analytics': [],
      'venues': ['user'],
      'sales-venues': ['user'],
      'warehouses': ['user'],
      'user-settings': ['user'],
      'access-management': ['user'],
      'product-investigation': [],
    },
  },

  [BETTER_TEST_MARKET_TENANT_ID]: {
    id: BETTER_TEST_MARKET_TENANT_ID,
    code: 'TEST_MARKET_BETTTER',
    name: 'Better Test Market',
    landingRoute: 'receiving',
    routes: {
      // venue-operations
      'tasks': ['user'],
      'receiving': ['user'],
      'ops-receiving': ['user'],
      'shrinkage': ['user'],
      'inventory-adjustments': ['user'],
      'bin-locations': ['user'],
      'stock-count': ['user'],
      'product-average-weight': ['user'],
      'venue-settings': [],

      // procurement
      'purchase-orders': ['user'],
      'vendors': ['user'],
      'finance': [],
      'override-management': [],

      // inventory-replenishment
      'forecast': [],
      'forecast-setup': [],
      'events': [],
      'product-relationship': [],
      'safety-stock-setup': [],
      'ordering-automation': [],
      'weekly-trends': [],
      'purchase-order-budgets': [],

      // selection
      'catalog': ['user'],
      'merchant-catalog': [],
      'product-families': ['user'],
      'assortment': ['user'],
      'selection-recommendations': [],
      'vsa': ['developer_wm'],

      // pricing-and-promotions
      'pricing': ['user'],
      'pricing-settings': ['user'],

      // others
      'analytics': [],
      'venues': ['user'],
      'sales-venues': ['user'],
      'warehouses': ['user'],
      'user-settings': ['user'],
      'access-management': ['user'],
      'product-investigation': [],
    },
  },

  [DASHMART_TENANT_ID]: {
    id: DASHMART_TENANT_ID,
    code: 'DASH_MART',
    name: 'DashMart',
    landingRoute: 'pricing',
    routes: {
      // venue-operations
      'tasks': [],
      'receiving': [],
      'ops-receiving': [],
      'shrinkage': [],
      'inventory-adjustments': [],
      'bin-locations': [],
      'stock-count': [],
      'product-average-weight': [],
      'venue-settings': [],

      // procurement
      'purchase-orders': ['developer_wm'],
      'vendors': [],
      'finance': [],
      'override-management': ['user'],

      // inventory-replenishment
      'forecast': ['user'],
      'forecast-setup': ['user'],
      'events': ['user'],
      'product-relationship': ['user'],
      'safety-stock-setup': ['user'],
      'ordering-automation': ['user'],
      'weekly-trends': ['user'],
      'purchase-order-budgets': [],

      // selection
      'catalog': ['user'],
      'merchant-catalog': [],
      'product-families': ['user'],
      'assortment': ['developer_wm'],
      'selection-recommendations': [],
      'vsa': ['developer_wm'],

      // pricing-and-promotions
      'pricing': ['user'],
      'pricing-settings': ['user'],

      // others
      'analytics': [],
      'venues': [],
      'sales-venues': [],
      'warehouses': [],
      'user-settings': ['user'],
      'access-management': ['user'],
      'product-investigation': [],
    },

    urls: {
      [ADMIN_URI]: ['developer_wm'],
      [VITE_MERCHANT_ADMIN_URI]: [],
      [USER_GUIDE_URI]: [],
    },
  },

  [DASHMART_TEST_TENANT_ID]: {
    id: DASHMART_TEST_TENANT_ID,
    code: 'DASH_MART_TEST',
    name: 'DashMart Test',
    landingRoute: 'pricing',
    routes: {
      // venue-operations
      'tasks': [],
      'receiving': [],
      'ops-receiving': [],
      'shrinkage': [],
      'inventory-adjustments': [],
      'bin-locations': [],
      'stock-count': [],
      'product-average-weight': [],
      'venue-settings': [],

      // procurement
      'purchase-orders': ['developer_wm'],
      'vendors': [],
      'finance': [],
      'override-management': ['user'],

      // inventory-replenishment
      'forecast': ['user'],
      'forecast-setup': ['user'],
      'events': ['user'],
      'product-relationship': ['user'],
      'safety-stock-setup': ['user'],
      'ordering-automation': ['user'],
      'weekly-trends': ['user'],
      'purchase-order-budgets': [],

      // selection
      'catalog': ['user'],
      'merchant-catalog': [],
      'product-families': ['user'],
      'assortment': ['developer_wm'],
      'selection-recommendations': [],
      'vsa': ['developer_wm'],

      // pricing-and-promotions
      'pricing': ['user'],
      'pricing-settings': ['user'],

      // others
      'analytics': [],
      'venues': [],
      'sales-venues': [],
      'warehouses': [],
      'user-settings': ['user'],
      'access-management': ['user'],
      'product-investigation': [],
    },

    urls: {
      [ADMIN_URI]: ['developer_wm'],
      [VITE_MERCHANT_ADMIN_URI]: [],
      [USER_GUIDE_URI]: [],
    },
  },

  [DOORDASH_DMAAS_TENANT_ID]: {
    id: DOORDASH_DMAAS_TENANT_ID,
    code: 'DOORDASH_DMAAS',
    name: 'DoorDash DMAAS',
    landingRoute: 'pricing',
    routes: {
      // venue-operations
      'tasks': [],
      'receiving': [],
      'ops-receiving': [],
      'shrinkage': [],
      'inventory-adjustments': [],
      'bin-locations': [],
      'stock-count': [],
      'product-average-weight': [],
      'venue-settings': [],

      // procurement
      'purchase-orders': [],
      'vendors': [],
      'finance': [],
      'override-management': [],

      // inventory-replenishment
      'forecast': [],
      'forecast-setup': [],
      'events': [],
      'product-relationship': [],
      'safety-stock-setup': [],
      'ordering-automation': [],
      'weekly-trends': [],
      'purchase-order-budgets': [],

      // selection
      'catalog': ['user'],
      'merchant-catalog': [],
      'product-families': ['user'],
      'assortment': ['developer_wm'],
      'selection-recommendations': [],
      'vsa': ['developer_wm'],

      // pricing-and-promotions
      'pricing': ['user'],
      'pricing-settings': ['user'],

      // others
      'analytics': [],
      'venues': [],
      'sales-venues': [],
      'warehouses': [],
      'user-settings': ['user'],
      'access-management': ['user'],
      'product-investigation': [],
    },

    urls: {
      [ADMIN_URI]: ['developer_wm'],
      [VITE_MERCHANT_ADMIN_URI]: [],
      [USER_GUIDE_URI]: [],
    },
  },

  [WOLT_MARKET_TENANT_ID]: {
    id: WOLT_MARKET_TENANT_ID,
    code: 'WOLT_MARKET',
    name: 'Wolt Market',
    landingRoute: 'receiving',
    routes: {
      // venue-operations
      'tasks': ['user'],
      'receiving': ['user'],
      'ops-receiving': ['user'],
      'shrinkage': ['user'],
      'inventory-adjustments': ['user'],
      'bin-locations': ['user'],
      'stock-count': ['user'],
      'product-average-weight': ['user'],
      'venue-settings': ['user'],

      // procurement
      'purchase-orders': ['user'],
      'vendors': ['user'],
      'finance': ['user'],
      'override-management': ['user'],

      // inventory-replenishment
      'forecast': ['user'],
      'forecast-setup': ['user'],
      'events': ['user'],
      'product-relationship': ['user'],
      'safety-stock-setup': ['user'],
      'ordering-automation': ['user'],
      'weekly-trends': ['user'],
      'purchase-order-budgets': ['user'],

      // selection
      'catalog': ['user'],
      'merchant-catalog': [],
      'product-families': ['user'],
      'assortment': ['user'],
      'selection-recommendations': ['user'],
      'vsa': ['developer_wm'],

      // pricing-and-promotions
      'pricing': ['user'],
      'pricing-settings': ['user'],

      // others
      'analytics': ['user'],
      'venues': ['user'],
      'sales-venues': ['user'],
      'warehouses': ['user'],
      'user-settings': ['user'],
      'access-management': ['user'],
      'product-investigation': ['user'],
    },

    urls: {
      [ADMIN_URI]: ['developer_wm'],
      [VITE_MERCHANT_ADMIN_URI]: ['user'],
      [USER_GUIDE_URI]: ['user'],
    },
  },

  [WOLT_GLOBAL_TENANT_ID]: {
    id: WOLT_GLOBAL_TENANT_ID,
    code: 'WOLT_GLOBAL',
    name: 'Wolt Global Catalog',
    landingRoute: 'catalog',
    routes: {
      // venue-operations
      'tasks': [],
      'receiving': [],
      'ops-receiving': [],
      'shrinkage': [],
      'inventory-adjustments': [],
      'bin-locations': [],
      'stock-count': [],
      'product-average-weight': [],
      'venue-settings': [],

      // procurement
      'purchase-orders': [],
      'vendors': [],
      'finance': [],
      'override-management': [],

      // inventory-replenishment
      'forecast': [],
      'forecast-setup': [],
      'events': [],
      'product-relationship': [],
      'safety-stock-setup': [],
      'ordering-automation': [],
      'weekly-trends': [],
      'purchase-order-budgets': [],

      // selection
      'catalog': ['user'],
      'merchant-catalog': ['user'],
      'product-families': ['user'],
      'assortment': [],
      'selection-recommendations': [],
      'vsa': ['developer_wm'],

      // pricing-and-promotions
      'pricing': [],
      'pricing-settings': [],

      // others
      'analytics': [],
      'venues': [],
      'sales-venues': [],
      'warehouses': [],
      'user-settings': ['user'],
      'access-management': ['user'],
      'product-investigation': [],
    },

    urls: {
      [ADMIN_URI]: ['developer_wm'],
      [VITE_MERCHANT_ADMIN_URI]: ['user'],
      [USER_GUIDE_URI]: ['user'],
    },
  },

  [CI_TEST_TENANT_ID]: {
    id: CI_TEST_TENANT_ID,
    code: 'CI_TEST',
    name: 'CI Test',
    landingRoute: 'receiving',
    routes: {
      // venue-operations
      'tasks': ['user'],
      'receiving': ['user'],
      'ops-receiving': ['user'],
      'shrinkage': ['user'],
      'inventory-adjustments': ['user'],
      'bin-locations': ['user'],
      'stock-count': ['user'],
      'product-average-weight': ['user'],
      'venue-settings': ['user'],

      // procurement
      'purchase-orders': ['user'],
      'vendors': ['user'],
      'finance': ['user'],
      'override-management': ['user'],

      // inventory-replenishment
      'forecast': ['user'],
      'forecast-setup': ['user'],
      'events': ['user'],
      'product-relationship': ['user'],
      'safety-stock-setup': ['user'],
      'ordering-automation': ['user'],
      'weekly-trends': ['user'],
      'purchase-order-budgets': ['user'],

      // selection
      'catalog': ['user'],
      'merchant-catalog': ['user'],
      'product-families': ['user'],
      'assortment': ['user'],
      'selection-recommendations': ['user'],
      'vsa': ['developer_wm'],

      // pricing-and-promotions
      'pricing': ['user'],
      'pricing-settings': ['user'],

      // others
      'analytics': ['user'],
      'venues': ['user'],
      'sales-venues': ['user'],
      'warehouses': ['user'],
      'user-settings': ['user'],
      'access-management': ['user'],
      'product-investigation': ['user'],
    },

    urls: {
      [ADMIN_URI]: ['developer_wm'],
      [VITE_MERCHANT_ADMIN_URI]: ['user'],
      [USER_GUIDE_URI]: ['user'],
    },
  },

  [DELIVEROO_TENANT_ID_PROD]: {
    id: DELIVEROO_TENANT_ID_PROD,
    code: 'DELIVEROO',
    name: 'Deliveroo',
    ...deliverooTenantConfig,
  },

  [DELIVEROO_TENANT_ID_PROD_TEST]: {
    id: DELIVEROO_TENANT_ID_PROD_TEST,
    code: 'DELIVEROO_TEST',
    name: 'Deliveroo Test',
    ...deliverooTenantConfig,
  },

  [DELIVEROO_TENANT_ID_DEV]: {
    id: DELIVEROO_TENANT_ID_DEV,
    code: 'DELIVEROO',
    name: 'Deliveroo',
    ...deliverooTenantConfig,
  },
};

export function getTenantConfig(tenantId: string | undefined): Tenant | DefaultTenant {
  return tenantId && tenantId in tenantConfig ? tenantConfig[tenantId] : defaultTenantConfig;
}
